import { AlertTriangle, Building2, Landmark } from 'lucide-react'

import { INSTITUTION_WIDE_BRANCH_ID, type ApprovalAmountBasis } from '@/api/approvals-types'
import type { Branch } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { Badge } from '@/components/ui/badge'
import { BASE_CURRENCY } from '@/lib/money'
import { cn } from '@/lib/utils'

import { branchDisplayName } from './branch-display'
import { describeExpiry, type ExpiryUrgency } from './expiry'
import { describeRequestType } from './request-types'

/**
 * The small pieces every approvals screen shares.
 *
 * All of them are pure functions of their props — no hooks — because they are rendered inside
 * table cells as well as inside panels, and a cell renderer is not a component boundary.
 */

const URGENCY_TONES: Record<ExpiryUrgency, 'neutral' | 'warning' | 'danger'> = {
  expired: 'danger',
  critical: 'danger',
  soon: 'warning',
  normal: 'neutral',
  unknown: 'warning',
}

/**
 * The deadline, as time left rather than as a timestamp.
 *
 * A checker does not need to know that a request lapses at 14:07 on Thursday; they need to know
 * whether it lapses before they get back from the branch visit. The exact instant is on the detail
 * panel, and in the title here, for anyone who does.
 */
export function ExpiryBadge({ expiresAtUtc }: { expiresAtUtc: string }) {
  const expiry = describeExpiry(expiresAtUtc)

  return (
    <Badge tone={URGENCY_TONES[expiry.urgency]} title={expiresAtUtc}>
      {expiry.label}
    </Badge>
  )
}

/**
 * The fields {@link ApprovalAmount} needs — shared by the checker's inbox row (`PendingApproval`)
 * and the maker's own submission row (`MyApprovalRequest`), which carry the same amount shape.
 */
export interface ApprovalAmountFields {
  amountBasis: ApprovalAmountBasis
  amountMinor: number | null
  amountRaw: number | string | null
  currencyCode: string | null
}

/**
 * The amount on a request.
 *
 * Three states, told apart deliberately. No amount at all is a dash — a role assignment has no
 * figure, and rendering a zero would invent one. A converted amount goes through
 * {@link MoneyDisplay} like every other figure in the application. An amount the server sent in a
 * form this client cannot read as money is said out loud, with the raw value, because a checker
 * countersigning a movement of money must never be shown a number that was guessed at.
 */
export function ApprovalAmount({ approval }: { approval: ApprovalAmountFields }) {
  if (approval.amountBasis === 'unreadable') {
    return (
      <span
        className="inline-flex items-center justify-end gap-1 text-xs text-danger-text"
        title={`The server sent ${JSON.stringify(approval.amountRaw)} where an amount was expected.`}
      >
        <AlertTriangle className="size-3.5" aria-hidden />
        Unreadable
      </span>
    )
  }

  return (
    <MoneyDisplay
      value={approval.amountMinor}
      currency={approval.currencyCode ?? BASE_CURRENCY}
      emphasis={approval.amountMinor === null ? 'muted' : 'normal'}
    />
  )
}

/** The operation in words, with the dotted code under it — the code is what gets read out. */
export function RequestTypeLabel({
  requestType,
  className,
}: {
  requestType: string
  className?: string
}) {
  const descriptor = describeRequestType(requestType)

  return (
    <span className={cn('flex min-w-0 flex-col', className)}>
      <span className="truncate text-sm font-medium text-heading">{descriptor.label}</span>
      {descriptor.isKnown ? (
        <span className="truncate font-mono text-2xs text-muted">{descriptor.code}</span>
      ) : (
        <span className="truncate text-2xs text-muted">
          Not an operation this build has a name for
        </span>
      )}
    </span>
  )
}

export function BranchCell({ branchId, branches }: { branchId: string; branches: Branch[] }) {
  const institutionWide = branchId === INSTITUTION_WIDE_BRANCH_ID
  const Icon = institutionWide ? Landmark : Building2

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-body">
      <Icon className="size-3.5 shrink-0 text-muted" aria-hidden />
      {branchDisplayName(branchId, branches)}
    </span>
  )
}
